import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Chip,
  Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import PrintIcon from "@mui/icons-material/Print";
import { NotificationManager } from "react-notifications";
import {
  GetInventoryLocations,
  SaveInventoryLocation,
  UpdateInventoryLocation,
  DeleteInventoryLocation,
  GetErrorMessage,
} from "../../../services/InventoryTrackerService";
import { PrintInventoryLabel } from "../../../../services/barcodeService";
import CommonLoader from "../../common/CommonLoader";
import DeleteConfirmation from "../../common/DeleteConfirmation";
import useSortedPaging from "./useSortedPaging";

// ENB-584: Warehouse > Rack > Shelf. A location's type decides which type its parent must be.
const LOCATION_TYPES = ["Warehouse", "Rack", "Shelf"];
const PARENT_TYPE = { Warehouse: null, Rack: "Warehouse", Shelf: "Rack" };

const emptyLocation = {
  id: 0,
  parentId: "",
  locationName: "",
  locationType: "Warehouse",
  barcode: "",
  sortOrder: 0,
  isActive: true,
};

const InventoryLocations = () => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  useEffect(() => {
    fetchLocations();
  }, []);

  const fetchLocations = async () => {
    try {
      setLoading(true);
      const response = await GetInventoryLocations(true);
      setLocations(response?.results || []);
    } catch (error) {
      console.error("Error fetching locations:", error);
      NotificationManager.error("Error fetching locations");
    } finally {
      setLoading(false);
    }
  };

  const filtered = locations.filter((location) =>
    [location.fullPath, location.barcode, location.locationType]
      .join(" ")
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  const columns = [
    { label: "Location", field: "fullPath" },
    { label: "Type", field: "locationType", rank: (type) => LOCATION_TYPES.indexOf(type) },
    { label: "Barcode", field: "barcode" },
    { label: "Status", field: "isActive", rank: (active) => (active ? 0 : 1) },
    { label: "Actions" },
  ];

  const { header, pageRows, pagination } = useSortedPaging(filtered, columns);

  const parentOptions = (type) =>
    locations.filter(
      (location) =>
        location.locationType === PARENT_TYPE[type] && location.id !== editing?.id
    );

  const handleSave = async () => {
    if (!editing.locationName?.trim()) {
      NotificationManager.error("Name is required");
      return;
    }
    if (PARENT_TYPE[editing.locationType] && !editing.parentId) {
      NotificationManager.error(
        `Pick the ${PARENT_TYPE[editing.locationType].toLowerCase()} this ${editing.locationType.toLowerCase()} sits in`
      );
      return;
    }

    const payload = {
      ...editing,
      parentId: PARENT_TYPE[editing.locationType] ? Number(editing.parentId) : null,
      locationName: editing.locationName.trim(),
      sortOrder: Number(editing.sortOrder) || 0,
    };

    setSaving(true);
    try {
      if (payload.id) {
        await UpdateInventoryLocation(payload);
        NotificationManager.success("Location updated");
      } else {
        await SaveInventoryLocation(payload);
        NotificationManager.success("Location added");
      }
      setEditing(null);
      fetchLocations();
    } catch (error) {
      NotificationManager.error(await GetErrorMessage(error, "Error saving location"));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    const location = toDelete;
    setToDelete(null);
    try {
      await DeleteInventoryLocation(location.id);
      NotificationManager.success(`${location.locationName} deleted`);
      fetchLocations();
    } catch (error) {
      NotificationManager.error(await GetErrorMessage(error, "Error deleting location"));
    }
  };

  const handlePrint = async (location) => {
    try {
      await PrintInventoryLabel("inventoryLocationLabel", {
        "{{name}}": location.fullPath,
        "{{barcode}}": location.barcode,
      });
      NotificationManager.success(`Label printed for ${location.locationName}`);
    } catch (error) {
      console.error("Error printing location label:", error);
      NotificationManager.error(error?.message || "Error printing label");
    }
  };

  const setField = (field, value) => {
    const updated = { ...editing, [field]: value };
    // A new type needs a parent of a different type, so the old pick no longer fits.
    if (field === "locationType") updated.parentId = "";
    setEditing(updated);
  };

  return (
    <div className="vlp-component-container">
      <span className="title">Inventory Locations </span>
      <hr />
      <div style={{ display: "flex", marginBottom: "16px", alignItems: "center" }}>
        <TextField
          label="Search"
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ marginRight: "16px", width: "80%" }}
        />
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => setEditing({ ...emptyLocation })}
        >
          New
        </Button>
      </div>

      <Paper style={{ marginRight: "20px" }}>
        <TableContainer>
          <Table sx={{ minWidth: 400 }} size="small">
            <TableHead sx={{ backgroundColor: (theme) => theme.palette.primary.main }}>
              {header}
            </TableHead>
            <TableBody>
              {pageRows.map((location, index) => (
                <TableRow
                  key={location.id}
                  sx={{ backgroundColor: index % 2 === 0 ? "rgba(0, 0, 0, 0.04)" : "white" }}
                >
                  <TableCell>{location.fullPath}</TableCell>
                  <TableCell>{location.locationType}</TableCell>
                  <TableCell>{location.barcode}</TableCell>
                  <TableCell>
                    {location.isActive ? (
                      <Chip label="Active" size="small" color="success" variant="outlined" />
                    ) : (
                      <Chip label="Inactive" size="small" variant="outlined" />
                    )}
                  </TableCell>
                  <TableCell>
                    <div style={{ minWidth: "120px" }}>
                      <IconButton
                        size="small"
                        style={{ color: "orange" }}
                        onClick={() => setEditing({ ...location, parentId: location.parentId ?? "" })}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <Tooltip title="Print barcode label">
                        <IconButton size="small" color="primary" onClick={() => handlePrint(location)}>
                          <PrintIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <IconButton size="small" style={{ color: "red" }} onClick={() => setToDelete(location)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {!loading && filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5}>No locations found.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {pagination}
      </Paper>

      <Dialog open={Boolean(editing)} onClose={() => setEditing(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing?.id ? "Edit Location" : "Add Location"}</DialogTitle>
        <DialogContent>
          {editing && (
            <Grid container spacing={1.5} sx={{ pt: 1 }}>
              <Grid item xs={6}>
                <TextField
                  size="small"
                  select
                  fullWidth
                  label="Type *"
                  value={editing.locationType}
                  onChange={(e) => setField("locationType", e.target.value)}
                >
                  {LOCATION_TYPES.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  size="small"
                  fullWidth
                  label="Name *"
                  value={editing.locationName}
                  onChange={(e) => setField("locationName", e.target.value)}
                  inputProps={{ maxLength: 100 }}
                  placeholder="Warehouse 1, Rack 6, Shelf 4..."
                />
              </Grid>
              {PARENT_TYPE[editing.locationType] && (
                <Grid item xs={12}>
                  <TextField
                    size="small"
                    select
                    fullWidth
                    label={`Inside ${PARENT_TYPE[editing.locationType]} *`}
                    value={editing.parentId}
                    onChange={(e) => setField("parentId", e.target.value)}
                  >
                    {parentOptions(editing.locationType).map((parent) => (
                      <MenuItem key={parent.id} value={parent.id}>
                        {parent.fullPath}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              )}
              <Grid item xs={6}>
                <TextField
                  size="small"
                  fullWidth
                  label="Barcode"
                  value={editing.barcode || ""}
                  onChange={(e) => setField("barcode", e.target.value)}
                  inputProps={{ maxLength: 50 }}
                  helperText="Leave blank to generate one"
                />
              </Grid>
              <Grid item xs={3}>
                <TextField
                  size="small"
                  fullWidth
                  type="number"
                  label="Sort Order"
                  value={editing.sortOrder}
                  onChange={(e) => setField("sortOrder", e.target.value)}
                />
              </Grid>
              <Grid item xs={3}>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={Boolean(editing.isActive)}
                      onChange={(e) => setField("isActive", e.target.checked)}
                    />
                  }
                  label="Active"
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setEditing(null)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : editing?.id ? "Update" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <DeleteConfirmation
        isOpen={Boolean(toDelete)}
        onClose={() => setToDelete(null)}
        onConfirm={handleDelete}
        title="Delete Confirmation"
        message={`Are you sure you want to delete ${toDelete?.fullPath}?`}
        color="red"
      />
      <CommonLoader loading={loading} />
    </div>
  );
};

export default InventoryLocations;
